import type { PhysicsEvent, GoalTag } from '../types';

/**
 * All sound is synthesized with Web Audio — no asset files. Each event
 * type maps to a small oscillator + envelope, with parameters varying
 * by hit speed / goal tag so the same kind of event sounds slightly
 * different each time. Lazy-init on the first user gesture so Chrome's
 * autoplay policy doesn't block us.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
let volume = 0.4;

/** Pre-built short noise buffer reused by all noise-flavored sounds.
 *  Synthesized once on init so per-sound calls are cheap. */
let noiseBuf: AudioBuffer | null = null;

export function isMuted(): boolean { return muted; }
export function setMuted(m: boolean): void {
  muted = m;
  applyVolume();
}
/** Set audio volume in [0, 1]. Independent of mute — when unmuted the
 *  master gain reflects this value. */
export function setVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
  applyVolume();
}
export function getVolume(): number { return volume; }

function applyVolume(): void {
  if (master) master.gain.value = muted ? 0 : volume;
}

/** Call from a user gesture (button click, keydown) before any sound. */
export function initAudio(): void {
  if (ctx) {
    // Already initialized — resume if suspended.
    if (ctx.state === 'suspended') void ctx.resume();
    return;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Ctor: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return;
  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : volume;
  master.connect(ctx.destination);

  // Pre-build a 0.5s white-noise buffer for re-use.
  const len = Math.floor(ctx.sampleRate * 0.5);
  noiseBuf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = noiseBuf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
}

/** Route every physics event to its corresponding sound. */
export function audioApplyEvents(events: readonly PhysicsEvent[]): void {
  if (!ctx || !master || muted) return;
  for (const ev of events) {
    if (ev.e === 'hit') {
      playHit(ev.speed);
    } else if (ev.e === 'wall') {
      // The same 'wall' event type fires for both flat walls and posts.
      // Use horizontal flag as a proxy: horizontal=false hits include
      // posts and pocket back-walls — give them a brighter ping than
      // flat top/bottom thumps.
      if (ev.horizontal) playWall(ev.speed);
      else playPost(ev.speed);
    } else if (ev.e === 'goal') {
      playGoal(ev.tag);
    } else if (ev.e === 'reset') {
      playReset();
    }
  }
}

function envelope(
  node: AudioNode,
  attack: number,
  peak: number,
  decay: number,
  release: number,
): GainNode {
  const c = ctx!;
  const g = c.createGain();
  const t0 = c.currentTime;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + attack);
  g.gain.linearRampToValueAtTime(peak * 0.6, t0 + attack + decay);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + attack + decay + release);
  node.connect(g);
  return g;
}

function playHit(speed: number): void {
  const c = ctx!;
  // Soft warm thump. Plastic-on-rubber feel: low sine body, gentle
  // pitch droop, lowpass to keep it from poking. No noise click — that
  // was the source of the harsh attack.
  const v = Math.min(1, speed / 18);
  const t0 = c.currentTime;

  const body = c.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(140 + v * 100, t0);
  body.frequency.exponentialRampToValueAtTime(70, t0 + 0.18);
  // Slightly longer attack softens the transient.
  const g = envelope(body, 0.008, 0.45 + v * 0.3, 0.05, 0.18);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 700;
  g.connect(lp).connect(master!);
  body.start(t0);
  body.stop(t0 + 0.3);

  // Sub-body for hard hits only — adds chest-thump weight without
  // making soft taps muddy.
  if (v > 0.5) {
    const sub = c.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 55;
    const sg = envelope(sub, 0.01, 0.2 * v, 0.04, 0.14);
    sg.connect(master!);
    sub.start(t0);
    sub.stop(t0 + 0.2);
  }
}

function playWall(speed: number): void {
  const c = ctx!;
  const v = Math.min(1, speed / 18);
  const t0 = c.currentTime;
  // Dampened wood-on-wood thud. Low sine, slow attack, heavy lowpass.
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(85 + v * 30, t0);
  osc.frequency.exponentialRampToValueAtTime(50, t0 + 0.14);
  const g = envelope(osc, 0.006, 0.22 + v * 0.15, 0.05, 0.12);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 350;
  g.connect(lp).connect(master!);
  osc.start(t0);
  osc.stop(t0 + 0.2);
}

function playPost(speed: number): void {
  const c = ctx!;
  const v = Math.min(1, speed / 18);
  const t0 = c.currentTime;
  // Tubular-bell ring: sine fundamental + a slightly-inharmonic upper
  // partial at quarter amplitude. The 2.4× ratio is what makes bells
  // sound metallic rather than musical (real bells aren't integer
  // harmonic). No square wave anywhere — that was the harsh culprit.
  const baseHz = 620 + v * 90;

  const fund = c.createOscillator();
  fund.type = 'sine';
  fund.frequency.value = baseHz;
  const fundG = envelope(fund, 0.003, 0.18 + v * 0.1, 0.05, 0.45);
  fundG.connect(master!);
  fund.start(t0);
  fund.stop(t0 + 0.55);

  const harm = c.createOscillator();
  harm.type = 'sine';
  harm.frequency.value = baseHz * 2.4;
  const harmG = envelope(harm, 0.003, 0.05 + v * 0.04, 0.04, 0.3);
  harmG.connect(master!);
  harm.start(t0);
  harm.stop(t0 + 0.4);
}

function playGoal(tag?: GoalTag): void {
  const c = ctx!;
  const t0 = c.currentTime;

  // Base whoomph: descending sine + noise burst.
  const sub = c.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(220, t0);
  sub.frequency.exponentialRampToValueAtTime(40, t0 + 0.45);
  const subG = envelope(sub, 0.01, 0.7, 0.1, 0.4);
  subG.connect(master!);
  sub.start(t0);
  sub.stop(t0 + 0.55);

  const burst = playNoiseBurst(0.25, 600, 0.35);
  if (burst) {
    const f = c.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1200;
    burst.connect(f).connect(master!);
  }

  // Per-tag flourish stacked on top.
  if (tag === 'banger') {
    // Extra low boom — louder and longer sub.
    const boom = c.createOscillator();
    boom.type = 'sawtooth';
    boom.frequency.setValueAtTime(80, t0);
    boom.frequency.exponentialRampToValueAtTime(35, t0 + 0.5);
    const bg = envelope(boom, 0.005, 0.55, 0.08, 0.45);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 400;
    bg.connect(lp).connect(master!);
    boom.start(t0);
    boom.stop(t0 + 0.6);
  } else if (tag === 'ace') {
    // Bright ascending arpeggio (clean, triumphant).
    [523, 659, 784, 988].forEach((f, i) => {
      const o = c.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = envelope(o, 0.005, 0.22, 0.05, 0.18);
      g.connect(master!);
      o.start(t0 + i * 0.06);
      o.stop(t0 + i * 0.06 + 0.3);
    });
  } else if (tag === 'long') {
    // Long sustained whistle that fades in and out.
    const whistle = c.createOscillator();
    whistle.type = 'sine';
    whistle.frequency.setValueAtTime(1320, t0);
    whistle.frequency.linearRampToValueAtTime(880, t0 + 0.7);
    const g = envelope(whistle, 0.1, 0.25, 0.3, 0.4);
    g.connect(master!);
    whistle.start(t0);
    whistle.stop(t0 + 0.85);
  } else if (tag === 'bank') {
    // Quick repeating tings before the boom — like banking.
    [0, 0.08, 0.16].forEach((delay) => {
      const o = c.createOscillator();
      o.type = 'square';
      o.frequency.value = 1100;
      const g = envelope(o, 0.002, 0.18, 0.02, 0.08);
      const filt = c.createBiquadFilter();
      filt.type = 'bandpass';
      filt.frequency.value = 1200;
      filt.Q.value = 10;
      g.connect(filt).connect(master!);
      o.start(t0 + delay);
      o.stop(t0 + delay + 0.12);
    });
  } else if (tag === 'own') {
    // Sad descending trombone — comedic.
    const trom = c.createOscillator();
    trom.type = 'sawtooth';
    trom.frequency.setValueAtTime(440, t0);
    trom.frequency.exponentialRampToValueAtTime(110, t0 + 0.6);
    const g = envelope(trom, 0.02, 0.35, 0.1, 0.35);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 800;
    g.connect(lp).connect(master!);
    trom.start(t0);
    trom.stop(t0 + 0.7);
  }
}

function playReset(): void {
  const c = ctx!;
  const t0 = c.currentTime;
  // Stalemate teleport — short whoosh + bright zap.
  const burst = playNoiseBurst(0.18, 3000, 0.3);
  if (burst) {
    const f = c.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = 1500;
    burst.connect(f).connect(master!);
  }
  const osc = c.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(2400, t0);
  osc.frequency.exponentialRampToValueAtTime(800, t0 + 0.18);
  const g = envelope(osc, 0.001, 0.2, 0.02, 0.15);
  g.connect(master!);
  osc.start(t0);
  osc.stop(t0 + 0.2);
}

function playNoiseBurst(duration: number, _centerHz: number, peak: number): GainNode | null {
  const c = ctx!;
  if (!noiseBuf) return null;
  const src = c.createBufferSource();
  src.buffer = noiseBuf;
  src.start();
  src.stop(c.currentTime + duration);
  const t0 = c.currentTime;
  const g = c.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  src.connect(g);
  return g;
}
